import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Alert,
  CircularProgress,
  Paper,
  Grid
} from '@mui/material';
import {
  SystemUpdateAlt as UpdateIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';

const APP_ID = 'rider-app';

const RiderAppUpdates = () => {
  const { actions } = useApp();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updateInfo, setUpdateInfo] = useState(null);
  const [form, setForm] = useState({
    version: '',
    bundleUrl: '',
    releaseNotes: ''
  });
  const [error, setError] = useState('');

  const fetchUpdateInfo = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${appConfig.api.baseURL}/app-updates/${APP_ID}`);
      const data = await response.json();

      if (data.success) {
        const d = data.data;
        setUpdateInfo(d);
        if (d) {
          setForm({
            version: d.version || '',
            bundleUrl: d.bundleUrl || '',
            releaseNotes: d.releaseNotes || ''
          });
        }
      } else {
        setError(data.message || 'Failed to fetch update info');
      }
    } catch (err) {
      console.error('Error fetching update info:', err);
      setError('Failed to fetch update info');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpdateInfo();
  }, []);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handlePublish = async () => {
    if (!form.version.trim()) {
      actions.showSnackbar('Version is required', 'error');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await fetch(`${appConfig.api.baseURL}/app-updates/${APP_ID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await response.json();

      if (data.success) {
        actions.showSnackbar('Rider app update published successfully', 'success');
        setUpdateInfo(data.data);
        fetchUpdateInfo();
      } else {
        setError(data.message || 'Failed to publish update');
        actions.showSnackbar(data.message || 'Failed to publish update', 'error');
      }
    } catch (err) {
      console.error('Error publishing update:', err);
      setError('Failed to publish update');
      actions.showSnackbar('Failed to publish update', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UpdateIcon />
          Rider App Updates
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUpdateInfo}
          disabled={loading}
          sx={{ borderRadius: 2 }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Publish New Update
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Configure update information for the CCC Rider app. Riders will receive updates based on these settings.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Version"
                    placeholder="e.g. 2.0.1"
                    value={form.version}
                    onChange={handleChange('version')}
                    required
                    fullWidth
                  />
                  <TextField
                    label="Bundle URL"
                    placeholder="https://your-cdn.com/rider-app/2.0.1.zip"
                    value={form.bundleUrl}
                    onChange={handleChange('bundleUrl')}
                    fullWidth
                    required
                    helperText="URL to the zip of build folder (index.html + static/ at root). Create: npm run build && cd build && zip -r ../dist.zip ."
                  />
                  <TextField
                    label="Release Notes"
                    placeholder="Bug fixes and improvements"
                    value={form.releaseNotes}
                    onChange={handleChange('releaseNotes')}
                    multiline
                    rows={4}
                    fullWidth
                  />
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                    onClick={handlePublish}
                    disabled={saving}
                    sx={{ alignSelf: 'flex-start', borderRadius: 2 }}
                  >
                    {saving ? 'Publishing...' : 'Publish Update'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Current Update
              </Typography>
              {updateInfo ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2"><strong>Version:</strong> {updateInfo.version}</Typography>
                  <Typography variant="body2"><strong>Bundle URL:</strong> {updateInfo.bundleUrl ? 'Set' : '—'}</Typography>
                  {updateInfo.releaseNotes && (
                    <Typography variant="body2" sx={{ mt: 1 }}><strong>Notes:</strong> {updateInfo.releaseNotes}</Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">No update configured yet</Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default RiderAppUpdates;
